import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import './DashboardCharts.css';

const DashboardCharts = () => {
  const [salesData, setSalesData] = useState([
    { day: 'Mon', revenue: 0 },
    { day: 'Tue', revenue: 0 },
    { day: 'Wed', revenue: 0 },
    { day: 'Thu', revenue: 0 },
    { day: 'Fri', revenue: 0 },
    { day: 'Sat', revenue: 0 },
    { day: 'Sun', revenue: 0 }
  ]);
  const [topItems, setTopItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSalesData();
    fetchTopItems();
  }, []);

  const fetchSalesData = async () => {
    try {
      // Get last 7 days
      const today = new Date();
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      sevenDaysAgo.setHours(0, 0, 0, 0);

      // Fetch orders from last 7 days
      const { data, error } = await supabase
        .from('orders')
        .select('total, created_at')
        .gte('created_at', sevenDaysAgo.toISOString())
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching sales data:', error);
        setLoading(false);
        return;
      }

      // Group by day of week
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const revenueByDay = {
        'Sun': 0,
        'Mon': 0,
        'Tue': 0,
        'Wed': 0,
        'Thu': 0,
        'Fri': 0,
        'Sat': 0
      };

      (data || []).forEach(order => {
        const orderDate = new Date(order.created_at);
        const dayName = dayNames[orderDate.getDay()];
        revenueByDay[dayName] += parseFloat(order.total || 0);
      });

      // Convert to array format
      const transformedData = dayNames.map(day => ({
        day,
        revenue: revenueByDay[day]
      }));

      setSalesData(transformedData);
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopItems = async () => {
    try {
      // Fetch all orders
      const { data: ordersData, error } = await supabase
        .from('orders')
        .select('items')
        .order('created_at', { ascending: false })
        .limit(100); // Get recent 100 orders for top items

      if (error) {
        console.error('Error fetching top items:', error);
        return;
      }

      // Count item occurrences
      const itemCounts = {};
      (ordersData || []).forEach(order => {
        const items = typeof order.items === 'string' 
          ? JSON.parse(order.items) 
          : order.items;
        
        items.forEach(item => {
          const itemName = item.name || item;
          if (!itemCounts[itemName]) {
            itemCounts[itemName] = 0;
          }
          itemCounts[itemName] += item.quantity || 1;
        });
      });

      // Sort and get top 5
      const topItemsArray = Object.entries(itemCounts)
        .map(([name, orders]) => ({ name, orders }))
        .sort((a, b) => b.orders - a.orders)
        .slice(0, 5);

      setTopItems(topItemsArray);
    } catch (error) {
      console.error('Error fetching top items:', error);
    }
  };

  const maxRevenue = Math.max(...salesData.map(d => d.revenue), 1);

  if (loading) {
    return (
      <div className="charts-section">
        <div className="chart-card sales-chart">
          <h3 className="chart-title">Sales Overview (Last 7 Days)</h3>
          <div className="chart-content">
            <div className="loading-state">Loading chart data...</div>
          </div>
        </div>
        <div className="chart-card top-items">
          <h3 className="chart-title">Top Selling Items</h3>
          <div className="loading-state">Loading top items...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="charts-section">
      <div className="chart-card sales-chart">
        <h3 className="chart-title">Sales Overview (Last 7 Days)</h3>
        <div className="chart-content">
          <div className="chart-container">
            <div className="chart-bars">
              {salesData.map((data, index) => {
                const height = maxRevenue > 0 ? (data.revenue / maxRevenue) * 100 : 0;
                return (
                  <div key={index} className="chart-bar-wrapper">
                    <div className="chart-bar" style={{ height: `${height}%` }}>
                      <span className="bar-value">Rs. {data.revenue.toLocaleString()}</span>
                    </div>
                    <span className="bar-label">{data.day}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="chart-legend">
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#004E89' }}></span>
              <span>Revenue (Rs.)</span>
            </div>
          </div>
        </div>
      </div>

      <div className="chart-card top-items">
        <h3 className="chart-title">Top Selling Items</h3>
        <div className="top-items-list">
          {topItems.length > 0 ? (
            topItems.map((item, index) => (
              <div key={index} className="top-item">
                <div className="item-rank">{index + 1}</div>
                <div className="item-details">
                  <div className="item-name">{item.name}</div>
                  <div className="item-orders">{item.orders} orders</div>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">No items data available</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardCharts;

